<!--
  烂尾单原因分类（原型：订单 - 订单跟进 - 烂尾单原因分类）

  全局字典表，不做数据权限；「原因」下拉的数据源是「烂尾单分析」表单。
-->
<template>
  <ContentWrap>
    <!-- 搜索工作栏 -->
    <el-form
      ref="queryFormRef"
      :inline="true"
      :model="queryParams"
      class="-mb-15px"
      label-width="90px"
    >
      <el-form-item label="关键字" prop="name">
        <el-input
          v-model="queryParams.name"
          class="!w-240px"
          clearable
          placeholder="请输入原因名称"
          @keyup.enter="handleQuery"
        />
      </el-form-item>
      <el-form-item label="备注" prop="remark">
        <el-input
          v-model="queryParams.remark"
          class="!w-240px"
          clearable
          placeholder="请输入备注"
          @keyup.enter="handleQuery"
        />
      </el-form-item>
      <el-form-item>
        <el-button @click="handleQuery">
          <Icon class="mr-5px" icon="ep:search" />
          搜索
        </el-button>
        <el-button @click="resetQuery">
          <Icon class="mr-5px" icon="ep:refresh" />
          重置
        </el-button>
        <el-button
          v-hasPermi="['crm:order-stall-reason:create']"
          type="primary"
          @click="openForm('create')"
        >
          <Icon class="mr-5px" icon="ep:plus" />
          新增
        </el-button>
        <el-button
          v-hasPermi="['crm:order-stall-reason:export']"
          :loading="exportLoading"
          plain
          type="success"
          @click="handleExport"
        >
          <Icon class="mr-5px" icon="ep:download" />
          导出
        </el-button>
      </el-form-item>
    </el-form>
  </ContentWrap>

  <!-- 列表 -->
  <ContentWrap>
    <el-table v-loading="loading" :data="list" :show-overflow-tooltip="true" :stripe="true">
      <el-table-column align="center" fixed="left" label="原因名称" prop="name" min-width="240" />
      <el-table-column align="center" label="备注" prop="remark" min-width="320" />
      <el-table-column
        align="center"
        fixed="right"
        :show-overflow-tooltip="false"
        label="操作"
        width="120"
      >
        <template #default="scope">
          <div class="crm-order-followup-action">
            <el-button
              v-hasPermi="['crm:order-stall-reason:update']"
              link
              type="primary"
              @click="openForm('update', scope.row.id)"
            >
              编辑
            </el-button>
            <el-button
              v-hasPermi="['crm:order-stall-reason:delete']"
              link
              type="danger"
              @click="handleDelete(scope.row.id)"
            >
              删除
            </el-button>
          </div>
        </template>
      </el-table-column>
    </el-table>
    <!-- 分页 -->
    <Pagination
      v-model:limit="queryParams.pageSize"
      v-model:page="queryParams.pageNo"
      :total="total"
      @pagination="getList"
    />
  </ContentWrap>

  <!-- 表单弹窗：添加/修改 -->
  <StallReasonForm ref="formRef" @success="getList" />
</template>

<script lang="ts" setup>
import download from '@/utils/download'
import { OrderStallReasonApi, OrderStallReasonVO } from '@/api/crm/orderStallReason'
import StallReasonForm from './StallReasonForm.vue'

defineOptions({ name: 'CrmOrderStallReason' })

const message = useMessage() // 消息弹窗
const { t } = useI18n() // 国际化

const loading = ref(true) // 列表的加载中
const total = ref(0) // 列表的总页数
const list = ref<OrderStallReasonVO[]>([]) // 列表的数据
const exportLoading = ref(false) // 导出的加载中

const queryParams = reactive<any>({
  pageNo: 1,
  pageSize: 10,
  name: '',
  remark: ''
})
const queryFormRef = ref() // 搜索的表单

/** 查询列表 */
const getList = async () => {
  loading.value = true
  try {
    const data = await OrderStallReasonApi.getOrderStallReasonPage(queryParams)
    list.value = data.list
    total.value = data.total
  } finally {
    loading.value = false
  }
}

/** 搜索按钮操作 */
const handleQuery = () => {
  queryParams.pageNo = 1
  getList()
}

/** 重置按钮操作 */
const resetQuery = () => {
  queryFormRef.value.resetFields()
  queryParams.name = ''
  queryParams.remark = ''
  handleQuery()
}

/** 添加/修改操作 */
const formRef = ref<InstanceType<typeof StallReasonForm>>()
const openForm = (type: string, id?: number) => {
  formRef.value?.open(type, id)
}

/** 删除按钮操作 */
const handleDelete = async (id: number) => {
  try {
    // 删除的二次确认
    await message.delConfirm()
    // 发起删除
    await OrderStallReasonApi.deleteOrderStallReason(id)
    message.success(t('common.delSuccess'))
    // 刷新列表
    await getList()
  } catch {}
}

/** 导出按钮操作 */
const handleExport = async () => {
  try {
    // 导出的二次确认
    await message.exportConfirm()
    // 发起导出
    exportLoading.value = true
    const data = await OrderStallReasonApi.exportOrderStallReason(queryParams)
    download.excel(data, '烂尾单原因分类.xlsx')
  } catch {
  } finally {
    exportLoading.value = false
  }
}

/** 初始化 **/
onMounted(() => {
  getList()
})
</script>

<style lang="scss" scoped>
.crm-order-followup-action {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;

  :deep(.el-button + .el-button) {
    margin-left: 0;
  }
}
</style>
